import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/common/utils/utils'

const Tabs = TabsPrimitive.Root

const tabsListVariants = cva('inline-flex items-center', {
    variants: {
        variant: {
            default: 'h-10 justify-center rounded-md bg-muted p-1 text-muted-foreground',
            underline: 'w-full p-0 h-auto flex justify-center',
            pills: 'h-auto p-0 justify-center',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
})

const tabsTriggerVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                default:
                    'rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
                underline:
                    'w-full py-3 rounded-b-none border-b-2 font-bold text-[#888] data-[state=active]:border-primary-light data-[state=active]:text-foreground',
                pills: 'w-full py-3 rounded-b-none font-semibold text-foreground data-[state=active]:bg-secondary-light',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
)

export interface TabsListProps
    extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
        VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
    ({ className, variant, ...props }, ref) => (
        <TabsPrimitive.List ref={ref} className={cn(tabsListVariants({ variant }), className)} {...props} />
    ),
)
TabsList.displayName = TabsPrimitive.List.displayName

export interface TabsTriggerProps
    extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
        VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
    ({ className, variant, ...props }, ref) => (
        <TabsPrimitive.Trigger ref={ref} className={cn(tabsTriggerVariants({ variant }), className)} {...props} />
    ),
)
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className,
        )}
        {...props}
    />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
